import type { MessageDescriptor } from 'react-intl';

import type { QuickInsertPreview } from '@atlaskit/editor-common/quick-insert/preview';
import {
	BRAINSTORMING_MENU_ITEM,
	DIAGRAM_MENU_ITEM,
	FLOWCHART_MENU_ITEM,
	RETROSPECTIVE_MENU_ITEM,
	ROADMAP_MENU_ITEM,
	WHITEBOARD_MENU_ITEM,
} from '@atlaskit/editor-common/quick-insert/keys';
import { CREATE_SECTION_RANK } from '@atlaskit/editor-common/quick-insert/rank';

import { messages } from './messages.ts';

export type WhiteboardSlashCommand = {
	/**
	 * Editor extension key - unique per slash command
	 */
	key: string;
	/**
	 * Internationalised title shown to user as title of slash command menu item
	 */
	title: MessageDescriptor;
	/**
	 * Internationalised description shown to user in slash command menu item
	 */
	description: MessageDescriptor;
	/**
	 * A known template key supported by whiteboards
	 * @see {@link https://developer.atlassian.com/cloud/confluence/rest/v2/api-group-whiteboard/#api-whiteboards-post-request}
	 */
	templateKey?: string;
	preview: QuickInsertPreview;
	/**
	 * optional sorting priority
	 */
	priority?: number;
};

const baseSlashCommands: Omit<WhiteboardSlashCommand, 'priority'>[] = [
	{
		key: 'whiteboard',
		title: messages.createWhiteboardTitle,
		description: messages.createWhiteboardDescription,
		preview: {
			image: {
				dark: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12OVHX.png',
				light: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12OVLT.png',
			},
		},
	},
	{
		key: 'retrospective',
		title: messages.createWhiteboardRetrospectiveTitle,
		description: messages.createWhiteboardRetrospectiveDescription,
		templateKey: 'simple-retrospective',
		preview: {
			image: {
				dark: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12OVI3.png',
				light: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12OVLO.png',
			},
		},
	},
	{
		key: 'diagram',
		title: messages.createWhiteboardDiagramTitle,
		description: messages.createWhiteboardDiagramDescription,
		templateKey: 'flow-chart',
		preview: {
			image: {
				dark: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12OVJZ.png',
				light: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12OVNL.png',
			},
		},
	},
	{
		key: 'flowchart',
		title: messages.createWhiteboardFlowchartTitle,
		description: messages.createWhiteboardFlowchartDescription,
		templateKey: 'flow-chart',
		preview: {
			image: {
				dark: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12OVIO.png',
				light: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12OVLQ.png',
			},
		},
	},
	{
		key: 'brainstorming',
		title: messages.createWhiteboardBrainstormingTitle,
		description: messages.createWhiteboardBrainstormingDescription,
		templateKey: 'brainwriting',
		preview: {
			image: {
				dark: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12OVI9.png',
				light: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12OVMV.png',
			},
		},
	},
	{
		key: 'roadmap',
		title: messages.createWhiteboardRoadmapTitle,
		description: messages.createWhiteboardRoadmapDescription,
		templateKey: 'product-roadmap',
		preview: {
			image: {
				dark: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12OVHW.png',
				light: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12OVLV.png',
			},
		},
	},
];

const withPriorities = (priorities: Readonly<Record<string, number>>): WhiteboardSlashCommand[] =>
	baseSlashCommands.map((command) => ({
		...command,
		...(priorities[command.key] !== undefined ? { priority: priorities[command.key] } : {}),
	}));

export const legacySlashCommands: WhiteboardSlashCommand[] = withPriorities({
	diagram: -200,
});

export const slashCommands: WhiteboardSlashCommand[] = withPriorities({
	whiteboard: CREATE_SECTION_RANK[WHITEBOARD_MENU_ITEM.key],
	diagram: CREATE_SECTION_RANK[DIAGRAM_MENU_ITEM.key],
	flowchart: CREATE_SECTION_RANK[FLOWCHART_MENU_ITEM.key],
	brainstorming: CREATE_SECTION_RANK[BRAINSTORMING_MENU_ITEM.key],
	retrospective: CREATE_SECTION_RANK[RETROSPECTIVE_MENU_ITEM.key],
	roadmap: CREATE_SECTION_RANK[ROADMAP_MENU_ITEM.key],
});
